import React, { useEffect, useMemo, useRef, useState } from "react";
import withRouter from "../../../components/Common/withRouter";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import "./MediaUpload.css";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { hasPermission } from "../../../helpers/permission";

const MediaUpload = () => {
  const baseurl = process.env.REACT_APP_BASE_URL;
const imgurl = process.env.REACT_APP_IMAGE_URL;
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [ImageDimensions, setImageDimensions] = useState("");
  const [imageFileSize, setImageFileSize] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [error, setError] = useState(null);

  const fetchFiles = async () => {
    try {
      setError(null);
      const response = await fetch(`${baseurl}/admin/media`);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setFiles(data);
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleAddMedia = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;
  
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files", file); // Note: same key name for each file
    });
  
    try {
      const response = await fetch(`${baseurl}/admin/media/upload`, {
        method: "POST",
        body: formData,
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${errorText}`);
      }
  
      const data = await response.json();
      if (data.success) {
        fetchFiles(); // refresh list
        toast.success("Media uploaded successfully!");
      } else {
        toast.error("Upload failed: Unexpected response.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Upload failed.");
    }
  };
  
  

  const handleEditFileName = (file) => {
    setEditingFile(file);
    setAltText(file.alternative_text || "");
    setCaption(file.caption || "");
    setTitle(file.title || "");
    setDescription(file.description || "");
    setFileUrl(file.fileurl);
    setImageFileSize(file.filesize);
    const img = new Image();
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      setImageDimensions(`${width}x${height}`);
      
    };
    img.src = `${imgurl}${file.fileurl}`;
    setEditModal(true);
  };

  const handleSaveFileName = async () => {
    try {
      const response = await fetch(`${baseurl}/admin/media/${editingFile.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          altText,
          caption,
          title,
          description,
        }),
      });
  
      if (!response.ok) throw new Error("Failed to update file");
  
      const updated = await response.json();
  
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === editingFile.id ? updated : file
        )
      );
      toast.success("File details updated!");
      setEditModal(false);
      fetchFiles();
    } catch (error) {
      toast.error("Failed to update media details.");
    }
  };
  

  const handleCopyURL = (file) => {
    navigator.clipboard.writeText(`${imgurl}`+file.original.fileurl);
    toast.success("URL copied to clipboard!");
  };

  const handleDownloadFile = (file) => {
    const link = document.createElement("a");
    link.href = `${imgurl}`+file.original.fileurl;
    link.download = file.original.filename;
    link.click();
  };

  const renderFilePreview = (file) => {
    const fileUrl = file?.fileurl || "";
    const fileType = file?.mimetype || "";
  
    if (fileType.startsWith("image/")) {
      return (
        <img
        src={`${imgurl}${fileUrl}`}
          alt={file.filename || "Media file"}
          style={{ width: "70px", height: "70px", objectFit: "cover" }}
        />
      );
    } else if (fileType === "application/pdf") {
      return <i className="mdi mdi-file-pdf" style={{ fontSize: "24px" }} />;
    } else {
      return <span>{file.filename || "Unknown file"}</span>;
    }
  };
  
  const handleDeleteFile = async () => {
    try {
      const response = await fetch(`${baseurl}/admin/media/${fileToDelete}`, {
        method: "DELETE",
      });
  
      if (!response.ok) throw new Error("Failed to delete file");
  
      setFiles(files.filter((file) => file.id !== fileToDelete));
      toast.success("File deleted successfully!");
      setDeleteModal(false);
    } catch (error) {
      toast.error("Failed to delete media.");
    }
  };
  
  const getFileTypeLabel = (mimetype = "") => {
  if (mimetype.startsWith("image/")) return "Image";
  if (mimetype === "application/pdf") return "PDF";
  if (mimetype.startsWith("video/")) return "Video";
  if (mimetype.startsWith("audio/")) return "Audio";
  if (
    mimetype.includes("word") ||
    mimetype.includes("officedocument")
  )
    return "Document";

  return "File";
};

  const columns = useMemo(
  () => [
    {
      Header: "ID",
      accessor: "id",
      width: "5%",
      Cell: ({ value }) => (
        <div className="text-body fw-bold">{value}</div>
      ),
    },
    {
      Header: "File",
      accessor: "file",
      width: "65%",
      Cell: ({ value, row }) => (
        <div className="d-flex media-content">
          {renderFilePreview(row.original)}
          <div className="ms-2">
            <h6 className="mb-0 text-truncate" >
              {row.original.title}
            </h6>
            <p className="text-secondary my-1">
              {getFileTypeLabel(row.original.mimetype)}
            </p>

            <div className="image-editing">
              {hasPermission("media.media.edit") && (
              <span onClick={() => handleEditFileName(row.original)}>Edit</span>
              )}
              {hasPermission("media.media.delete") && (
              <p
                onClick={() => {
                  setFileToDelete(row.original.id);
                  setDeleteModal(true);
                }}
              >
                Delete Permanently
              </p>
              )}
              <span>View</span>
            </div>

            <div className="image-editing-copy">
              <span onClick={() => handleCopyURL(row)}>Copy URL</span>
              <p onClick={() => handleDownloadFile(row)}>Download file</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      Header: "Type",
      accessor: "mimetype",
      width: "10%",
      Cell: ({ value }) => (
        <div className="text-body fw-bold">
          {getFileTypeLabel(value)}
        </div>
      ),
    },
    {
      Header: "Date",
      accessor: "uploadedAt",
      width: "10%",
      Cell: ({ row }) => (
        <div className="text-body fw-bold">
          {row.original.uploaded_on}
        </div>
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      width: "10%",
      Cell: ({ row }) => (
        <div className="d-flex gap-3">
          {hasPermission("media.media.delete") && (
          <Link
            to="#"
            className="text-danger"
            onClick={() => {
              setFileToDelete(row.original.id);
              setDeleteModal(true);
            }}
          >
            <i className="bx bx-trash-alt font-size-20"></i>
          </Link>
          )}
        </div>
      ),
    },
  ],
  [files]
);


  document.title = "Media Gallery | Vekart";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Media" breadcrumbItem="Media Gallery" />
        <Card>
          <CardBody>
            {files?.length === 0 ? (
              <div className="text-center my-4">
                <h5>No media selected</h5>
                <Button color="primary" className="mt-2 me-2" onClick={handleButtonClick}>
                  <i className="mdi mdi-plus me-1" /> Upload Media
                </Button>
                <Input
                  type="file"
                  innerRef={fileInputRef}
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.xlsx,.xls"
                  className="d-none"
                  onChange={handleAddMedia}
                />
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={files}
                isAddMedia={true}
                isGlobalFilter={true}
                isAddOptions={false}
                customPageSize={10}
                className="custom-header-css"
                handleButtonClick={handleButtonClick}
                handleAddMedia={handleAddMedia}
                fileInputRef={fileInputRef}
              />
            )}
          </CardBody>
        </Card>
        {/* Modal for Delete Image */}
        <Modal
            size="lg"
            isOpen={deleteModal}
            toggle={() => setDeleteModal(false)}
            centered={true}
          >
            <div className="modal-content">
              <ModalBody className="px-4 py-5 text-center">
                <button
                  type="button"
                  onClick={() => setDeleteModal(false)}
                  className="btn-close position-absolute end-0 top-0 m-3"
                ></button>
                <div className="avatar-sm mb-4 mx-auto">
                  <div className="avatar-title bg-primary text-primary bg-opacity-10 font-size-20 rounded-3">
                    <i className="mdi mdi-trash-can-outline"></i>
                  </div>
                </div>
                <p className="text-muted font-size-16 mb-4">
                  If image currently use in anywhere in the system, button would
                  you like to permanently delete from the server
                </p>

                <div className="hstack gap-2 justify-content-center mb-0">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setDeleteModal(false)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteFile}
                  >
                    Delete Now
                  </button>
                </div>
              </ModalBody>
            </div>
          </Modal>

          {/* Modal for Editing Image Details */}
          <Modal
            size="xl"
            isOpen={editModal}
            toggle={() => setEditModal(false)}
            scrollable
          >
            <ModalHeader toggle={() => setEditModal(false)} tag="h4">
              Attachment details
            </ModalHeader>
            <div className="modal-content">
              <ModalBody className="px-4 py-4">
                {editingFile && (
                  <>
                    <Row>
                      <Col lg={6} sm={12}>
                        <div className="d-grid align-items-center justify-content-center">
                          <img
                          src={`${imgurl}${editingFile.fileurl}`}
                          alt={editingFile.filename || "Media file"}
                            
                            style={{
                              width: "300px",
                              height: "300px",
                              objectFit: "cover",
                            }}
                          />
                          {/* <div className="text-center">
                            <input
                              type="file"
                              className="form-control"
                              onChange={handleImageChange}
                            />
                          </div> */}
                        </div>
                      </Col>
                      <Col lg={6} sm={12}>
                        <p className="mb-1">
                          <strong>Uploaded on: </strong>
                          {editingFile.uploaded_on}
                        </p>
                        <p className="mb-1">
                          <strong>File Name: </strong> {editingFile.filename}
                        </p>
                        <p className="mb-1">
                          <strong>File Type: </strong> {editingFile.mimetype}
                        </p>
                        <p className="mb-1">
                          <strong>File Size: </strong> {Math.round(imageFileSize / 1024)} KB
                        </p>
                        <p className="mb-4">
                          <strong>Dimensions: </strong> {ImageDimensions}
                        </p>
                        <div className="mb-3 d-flex align-items-center">
                          <Label className="mb-0" style={{ width: "120px" }}>
                            Alternative Text
                          </Label>
                          <Input
                            type="text"
                            value={altText}
                            onChange={(e) => setAltText(e.target.value)}
                            style={{ flex: 1 }}
                          />
                        </div>
                        <div className="mb-3 d-flex align-items-center">
                          <Label className="mb-0" style={{ width: "120px" }}>
                            File URL
                          </Label>
                          <Input
                            type="text"
                            value={fileUrl}
                            readOnly
                            style={{ flex: 1 }}
                          />
                        </div>
                        <div className="mb-3 d-flex align-items-center">
                          <Label className="mb-0" style={{ width: "120px" }}>
                            Caption
                          </Label>
                          <Input
                            type="text"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            style={{ flex: 1 }}
                          />
                        </div>
                        <div className="mb-3 d-flex align-items-center">
                          <Label className="mb-0" style={{ width: "120px" }}>
                            Title
                          </Label>
                          <Input
                            type="text"
                            value={title}
                            
                            onChange={(e) => setTitle(e.target.value)}
                            style={{ flex: 1 }}
                          />
                        </div>
                        <div className="mb-3 d-flex align-items-center">
                          <Label className="mb-0" style={{ width: "120px" }}>
                            Description
                          </Label>
                          <Input
                            type="textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{ flex: 1 }}
                          />
                        </div>

                        <div className="d-flex justify-content-end">
                          <Button color="primary" onClick={handleSaveFileName}>
                            Save Changes
                          </Button>
                          <Button
                            color="secondary"
                            onClick={() => setEditModal(false)}
                            className="ms-2"
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
              </ModalBody>
            </div>
          </Modal>
      </Container>
    </div>
  );
};

export default withRouter(MediaUpload);
